import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { AxiosError } from 'axios'

import { apiZodios } from '~api/apiZodios'
import { Button, Icon, IconButton, Toast, Typography, WizardStepper } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import {
  useCreateVoicemailProcessMutation,
  useGetOrganizationAISettingsQuery,
  useGetProcessQuery,
  usePreviewVoiceMutation,
} from '~redux/slices/apiSlice'
import {
  type AIPhoneLineTypeEnum,
  type ELVoice,
  type EnrichedAIPhoneLineConfig,
  HttpStatus,
  type PhonicVoice,
  phoneWithExtension,
} from '~types'
import { getUserTimezone } from '~utils/dateUtils'

import { useAISettings } from '../../AISettingsContext'

import './AIPhoneLineDialog.scss'
import PhoneSettingsStep from './PhoneSettingsStep'
import ScheduleSettingsStep from './ScheduleSettingsStep'
import {
  getPhoneNumber,
  transferLineFields,
  validatePhoneSettings,
  validateScheduleSettings,
} from './utils'
import {
  DEFAULT_ELEVEN_LABS_VOICE_SETTINGS,
  DEFAULT_PHONE_LINE_TYPE,
  DEFAULT_PHONIC_VOICE_SETTINGS,
  type ElevenLabsVoiceSettings,
  type PhonicVoiceSettings,
  phonicSettingsFromMetadata,
  phonicSettingsToRequestParams,
} from './voiceSettings'
import VoiceSettingsStep from './VoiceSettingsStep'

const LIST_VOICES_LIMIT = 100

type WizardStep = 'phone' | 'voice' | 'schedule'

const PHONE_STEP = { label: 'Phone', key: 'phone' as const }
const VOICE_STEP = { label: 'Voice', key: 'voice' as const }
const SCHEDULE_STEP = { label: 'Schedule', key: 'schedule' as const }
const WIZARD_STEPS = [PHONE_STEP, VOICE_STEP, SCHEDULE_STEP]

interface AIPhoneLineDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: (successMessage: string) => void
  isUpdate: boolean
  aiPhoneLineId?: string
}

const AIPhoneLineDialog: React.FC<AIPhoneLineDialogProps> = ({
  open,
  onClose,
  onSuccess,
  isUpdate,
  aiPhoneLineId,
}) => {
  const { selectedOrganization } = useAISettings()
  const user = useSelector((state: RootState) => state?.auth.user)
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)

  const [currentStep, setCurrentStep] = useState<WizardStep>('phone')
  const stepIndex = Math.max(
    0,
    WIZARD_STEPS.findIndex((step) => step.key === currentStep),
  )
  const isLastStep = stepIndex === WIZARD_STEPS.length - 1

  const defaultTimezone = getUserTimezone('America/New_York')

  // Phone step
  const [number, setNumber] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [isValidPhone, setIsValidPhone] = useState<boolean>(false)
  const [defaultGreetingMessage, setDefaultGreetingMessage] = useState<string>('')
  const [defaultTransferLine, setDefaultTransferLine] = useState<string>('')
  const [isValidDefaultTransferPhone, setIsValidDefaultTransferPhone] = useState<boolean>(true)

  // Voice step
  const [phoneLineType, setPhoneLineType] = useState<AIPhoneLineTypeEnum>(DEFAULT_PHONE_LINE_TYPE)
  const [voices, setVoices] = useState<ELVoice[]>([])
  const [elevenLabsSettings, setElevenLabsSettings] = useState<ElevenLabsVoiceSettings>(
    DEFAULT_ELEVEN_LABS_VOICE_SETTINGS,
  )
  const [phonicVoices, setPhonicVoices] = useState<PhonicVoice[]>([])
  const [phonicSettings, setPhonicSettings] = useState<PhonicVoiceSettings>(
    DEFAULT_PHONIC_VOICE_SETTINGS,
  )

  // Schedule step
  const [phoneLineConfigurations, setPhoneLineConfigurations] = useState<
    EnrichedAIPhoneLineConfig[]
  >([])
  const [phoneLineHolidays, setPhoneLineHolidays] = useState<EnrichedAIPhoneLineConfig[]>([])

  // Dialog state
  const [error, setError] = useState<string | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState<string>('')
  const [snackbarType, setSnackbarType] = useState<'error' | 'warning'>('warning')
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showNoTransferFallbackWarning, setShowNoTransferFallbackWarning] = useState(false)
  const [initialFormSnapshot, setInitialFormSnapshot] = useState<string>('')
  const shouldCaptureSnapshot = useRef(false)

  const { data: aiSettings, refetch: refetchAiSettings } = useGetOrganizationAISettingsQuery(
    { organization_handle: selectedOrganization?.handle },
    { skip: !open || !selectedOrganization?.handle },
  )
  const voicemailProcessId = aiSettings?.organization_ai_settings?.voicemail_process_id

  const { data: voicemailProcessData } = useGetProcessQuery(
    { process_info_args: { process_id: voicemailProcessId } },
    { skip: !voicemailProcessId || !selectedOrganization?.handle },
  )
  const voicemailProcess = voicemailProcessData
    ? {
        name: voicemailProcessData.process.name,
        url: `/organizations/${selectedOrganization?.handle}/workflows/${voicemailProcessData.process.code}/history`,
        isDraft: voicemailProcessData.process.status === 'draft',
      }
    : null

  const [createVoicemailProcess, { isLoading: isCreatingVoicemailProcess }] =
    useCreateVoicemailProcessMutation()

  const [previewVoice, { isLoading: isPreviewLoading }] = usePreviewVoiceMutation()
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const [isPreviewPlaying, setIsPreviewPlaying] = useState<boolean>(false)

  const stopPreviewAudio = useCallback((): void => {
    if (previewAudioRef.current) {
      URL.revokeObjectURL(previewAudioRef.current.src)
      previewAudioRef.current.pause()
      previewAudioRef.current.src = ''
      previewAudioRef.current = null
    }
    setIsPreviewPlaying(false)
  }, [])

  const handlePreviewGreeting = async (): Promise<void> => {
    const { voiceId, speed, stability, similarity } = elevenLabsSettings
    if (!user?.id || !selectedOrganization?.id || !voiceId || !defaultGreetingMessage.trim()) return
    const wasPlaying = previewAudioRef.current !== null
    stopPreviewAudio()
    if (wasPlaying) return

    const result = await previewVoice({
      user_id: user.id,
      organization_identity_id: selectedOrganization.id,
      voice_id: voiceId,
      text: defaultGreetingMessage,
      ...(speed !== null && { speed }),
      ...(stability !== null && { stability }),
      ...(similarity !== null && { similarity_boost: similarity }),
    })

    if ('error' in result) {
      stopPreviewAudio()
      return
    }

    const audio = new Audio(result.data)
    previewAudioRef.current = audio
    setIsPreviewPlaying(true)
    audio.addEventListener('ended', () => {
      if (previewAudioRef.current === audio) {
        URL.revokeObjectURL(audio.src)
        setIsPreviewPlaying(false)
        previewAudioRef.current = null
      }
    })
    audio.addEventListener('error', () => {
      if (previewAudioRef.current === audio) stopPreviewAudio()
    })
    audio.play()
  }

  const getCurrentFormSnapshot = (): string =>
    JSON.stringify({
      number,
      name,
      phoneLineType,
      defaultGreetingMessage,
      defaultTransferLine,
      phoneLineConfigurations,
      phoneLineHolidays,
      elevenLabsSettings,
      phonicSettings,
    })

  const hasFormChanged = (): boolean =>
    isUpdate && initialFormSnapshot !== '' && getCurrentFormSnapshot() !== initialFormSnapshot

  const fetchVoices = async (): Promise<void> => {
    if (!selectedOrganization?.id || !user) return
    try {
      const response = await apiZodios.ai_listVoices({
        user_id: user.id,
        organization_identity_id: selectedOrganization.id,
        limit: LIST_VOICES_LIMIT,
      })
      setVoices(response.voices || [])
    } catch (error) {
      console.error('Error fetching voices:', error)
      setVoices([])
    }
  }

  const fetchPhonicVoices = async (): Promise<void> => {
    if (!selectedOrganization?.id || !user) return
    try {
      const response = await apiZodios.ai_listPhonicVoices({
        user_id: user.id,
        organization_identity_id: selectedOrganization.id,
      })
      setPhonicVoices(response.voices || [])
    } catch (error) {
      console.error('Error fetching Phonic voices:', error)
      setPhonicVoices([])
    }
  }

  useEffect(() => {
    if (shouldCaptureSnapshot.current) {
      setInitialFormSnapshot(getCurrentFormSnapshot())
      shouldCaptureSnapshot.current = false
    }
  })

  useEffect(() => {
    if (open) {
      fetchVoices()
      fetchPhonicVoices()
    }
    if (aiPhoneLineId) {
      fetchAIPhoneLine(aiPhoneLineId)
    } else {
      resetForm()
    }
  }, [aiPhoneLineId, open])

  const resetForm = (): void => {
    setCurrentStep('phone')
    setNumber('')
    setName('')
    setIsValidPhone(false)
    setError(null)
    setPhoneLineType(DEFAULT_PHONE_LINE_TYPE)
    setDefaultGreetingMessage('')
    setDefaultTransferLine('')
    setIsValidDefaultTransferPhone(true)
    setPhoneLineConfigurations([])
    setPhoneLineHolidays([])
    setElevenLabsSettings(DEFAULT_ELEVEN_LABS_VOICE_SETTINGS)
    setPhonicSettings(DEFAULT_PHONIC_VOICE_SETTINGS)
    setInitialFormSnapshot('')
    setShowNoTransferFallbackWarning(false)
    stopPreviewAudio()
  }

  const fetchAIPhoneLine = async (id: string): Promise<void> => {
    if (!selectedOrganization?.id || !user) return
    try {
      const response = await apiZodios.ai_listAIPhoneLines({
        user_id: user.id,
        organization_identity_id: selectedOrganization.id,
      })
      const aiPhoneLine = response.ai_phone_lines.find((line) => line.id === id)
      if (!aiPhoneLine) return

      const formattedNumber = aiPhoneLine.number.startsWith('+1')
        ? aiPhoneLine.number.substring(2)
        : aiPhoneLine.number

      setNumber(formattedNumber)
      setName(aiPhoneLine.name || '')
      setIsValidPhone(!!formattedNumber)
      setPhoneLineType((aiPhoneLine.type as AIPhoneLineTypeEnum) || DEFAULT_PHONE_LINE_TYPE)
      setElevenLabsSettings({
        voiceId: aiPhoneLine.voice_id || null,
        speed: aiPhoneLine.voice_speed ?? null,
        stability: aiPhoneLine.voice_stability ?? null,
        similarity: aiPhoneLine.voice_similarity ?? null,
      })
      setPhonicSettings(phonicSettingsFromMetadata(aiPhoneLine.metadata))

      const configs = aiPhoneLine.configs || []
      const defaultConfig = configs.find(
        (config) =>
          config.start_time === '00:00:00' &&
          config.end_time === '23:59:59' &&
          Array.isArray(config.days_of_week) &&
          config.days_of_week.length === 7 &&
          config.priority === -1,
      )
      const holidayConfigs = configs.filter((config) => config.inactive_dates != null)
      const regularConfigs = configs
        .filter((config) => config.priority !== -1 && config.inactive_dates == null)
        .sort((a, b) => (b.priority || 0) - (a.priority || 0))

      if (defaultConfig) {
        setDefaultGreetingMessage(defaultConfig.greeting_message || '')
        const transferNumber = phoneWithExtension(
          defaultConfig.default_transfer_number,
          defaultConfig.default_transfer_extension,
        )
        setDefaultTransferLine(transferNumber)
        setIsValidDefaultTransferPhone(transferNumber === '' || !!getPhoneNumber(transferNumber))
      }

      setPhoneLineConfigurations(regularConfigs)
      setPhoneLineHolidays(holidayConfigs)
      shouldCaptureSnapshot.current = true
    } catch (error) {
      console.error('Error fetching AI phone line:', error)
    }
  }

  const handleOnClose = (): void => {
    resetForm()
    onClose()
  }

  const handlePhoneNumberChange = (newPhoneString: string, newPhoneIsValid: boolean): void => {
    setNumber(newPhoneString)
    setIsValidPhone(newPhoneIsValid)
  }

  const handleDefaultTransferPhoneChange = (
    newPhoneString: string,
    newPhoneIsValid: boolean,
  ): void => {
    setDefaultTransferLine(newPhoneString)
    setIsValidDefaultTransferPhone(newPhoneString === '' || newPhoneIsValid)
  }

  const handleDefaultGreetingMessageChange = (message: string): void => {
    setDefaultGreetingMessage(message)
    stopPreviewAudio()
  }

  const handlePhoneLineTypeChange = (type: AIPhoneLineTypeEnum): void => {
    setPhoneLineType(type)
    setElevenLabsSettings((prev) => ({ ...prev, voiceId: null }))
    stopPreviewAudio()
  }

  const createDefaultConfig = (): EnrichedAIPhoneLineConfig => {
    const tempUUID = crypto.randomUUID()
    return {
      id: tempUUID,
      phone_line: tempUUID,
      name: 'Default Configuration',
      greeting_message: defaultGreetingMessage,
      ...transferLineFields(defaultTransferLine),
      start_time: '00:00:00',
      end_time: '23:59:59',
      days_of_week: [0, 1, 2, 3, 4, 5, 6],
      timezone: defaultTimezone,
      priority: -1,
    }
  }

  const phoneError = validatePhoneSettings({
    number,
    isValidPhone,
    defaultTransferLine,
    isValidDefaultTransferPhone,
    defaultGreetingMessage,
  })
  const scheduleError = validateScheduleSettings(phoneLineConfigurations, phoneLineHolidays)
  const isValidForm = phoneError === null && scheduleError === null

  useEffect(() => {
    setError(null)
  }, [
    number,
    isValidPhone,
    isValidDefaultTransferPhone,
    phoneLineConfigurations,
    phoneLineHolidays,
  ])

  const handleStepClick = (index: number): void => {
    const step = WIZARD_STEPS[index]
    if (step) setCurrentStep(step.key)
  }

  const handleNext = (): void => {
    if (currentStep === 'phone' && phoneError) {
      setError(phoneError)
      return
    }
    const next = WIZARD_STEPS[stepIndex + 1]
    if (next) setCurrentStep(next.key)
  }

  const handleBack = (): void => {
    const prev = WIZARD_STEPS[stepIndex - 1]
    if (prev) setCurrentStep(prev.key)
  }

  const handleSave = async (): Promise<void> => {
    const validationError = phoneError ?? scheduleError
    if (validationError) {
      setError(validationError)
      setCurrentStep(phoneError ? 'phone' : 'schedule')
      return
    }
    if (!selectedOrganization?.id || !user) return
    if (!defaultTransferLine.trim() && !voicemailProcessId) {
      setShowNoTransferFallbackWarning(true)
      return
    }
    await executeSave()
  }

  const executeSave = async (): Promise<void> => {
    if (!selectedOrganization?.id || !user) return
    setIsSaving(true)
    try {
      const numberComponents = getPhoneNumber(number)
      if (!numberComponents) {
        setError('Invalid phone number format')
        return
      }
      const defaultConfig = createDefaultConfig()
      const allConfigs = [defaultConfig, ...phoneLineConfigurations, ...phoneLineHolidays]
      const phonicParams = phonicSettingsToRequestParams(phonicSettings)
      const elevenLabsParams = {
        elevenlabs_voice_similarity: elevenLabsSettings.similarity,
        elevenlabs_voice_stability: elevenLabsSettings.stability,
        elevenlabs_voice_speed: elevenLabsSettings.speed,
      }

      if (isUpdate) {
        if (!aiPhoneLineId) return
        await apiZodios.ai_editAIPhoneLine({
          user_id: user.id,
          organization_identity_id: selectedOrganization.id,
          ai_phone_line_id: aiPhoneLineId,
          number: '+1' + numberComponents.phoneNumber,
          name: name || '',
          type: phoneLineType,
          configs: allConfigs,
          elevenlabs_voice_id: elevenLabsSettings.voiceId || undefined,
          ...elevenLabsParams,
          ...phonicParams,
        })
        onSuccess?.('Successfully updated AI phone line')
      } else {
        await apiZodios.ai_addAIPhoneLine({
          user_id: user.id,
          organization_identity_id: selectedOrganization.id,
          number: '+1' + numberComponents.phoneNumber,
          name: name || '',
          type: phoneLineType,
          configs: allConfigs,
          elevenlabs_voice_id: elevenLabsSettings.voiceId || null,
          ...elevenLabsParams,
          ...phonicParams,
        })
        onSuccess?.('Successfully added AI phone line')
      }
      resetForm()
      onClose()
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        setSnackbarMessage(
          error.response?.status === HttpStatus.CONFLICT
            ? 'Phone number already exists'
            : 'Failed to save AI phone line',
        )
        setSnackbarType(error.response?.status === HttpStatus.CONFLICT ? 'warning' : 'error')
        setIsSnackbarOpen(true)
      } else {
        console.error('Error saving AI phone line:', error)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreateVoicemailProcess = async (): Promise<void> => {
    if (!selectedOrganization?.id || !user) return
    const result = await createVoicemailProcess({
      user_id: user.id,
      organization_identity_id: selectedOrganization.id,
    })
    if (result?.error) {
      setSnackbarMessage('Failed to create voicemail workflow')
      setSnackbarType('error')
      setIsSnackbarOpen(true)
      return
    }
    await refetchAiSettings()
    onSuccess?.('Successfully created voicemail workflow')
  }

  const renderStep = (): React.ReactNode => {
    switch (currentStep) {
      case 'phone':
        return (
          <PhoneSettingsStep
            number={number}
            onNumberChange={handlePhoneNumberChange}
            name={name}
            onNameChange={setName}
            defaultTransferLine={defaultTransferLine}
            onDefaultTransferLineChange={handleDefaultTransferPhoneChange}
            defaultGreetingMessage={defaultGreetingMessage}
            onDefaultGreetingMessageChange={handleDefaultGreetingMessageChange}
            voicemailProcess={voicemailProcess}
            isCreatingVoicemailProcess={isCreatingVoicemailProcess}
            onCreateVoicemailProcess={handleCreateVoicemailProcess}
          />
        )
      case 'voice':
        return (
          <VoiceSettingsStep
            phoneLineType={phoneLineType}
            onPhoneLineTypeChange={handlePhoneLineTypeChange}
            canChangePhoneLineType={Boolean(isSuper)}
            greetingMessage={defaultGreetingMessage}
            elevenLabsVoices={voices}
            elevenLabsSettings={elevenLabsSettings}
            onElevenLabsSettingsChange={(settings) =>
              setElevenLabsSettings((prev) => ({ ...prev, ...settings }))
            }
            isPreviewLoading={isPreviewLoading}
            isPreviewPlaying={isPreviewPlaying}
            onPreviewGreeting={handlePreviewGreeting}
            phonicVoices={phonicVoices}
            phonicSettings={phonicSettings}
            onPhonicSettingsChange={(settings) =>
              setPhonicSettings((prev) => ({ ...prev, ...settings }))
            }
          />
        )
      case 'schedule':
        return (
          <ScheduleSettingsStep
            phoneLineConfigurations={phoneLineConfigurations}
            setPhoneLineConfigurations={setPhoneLineConfigurations}
            phoneLineHolidays={phoneLineHolidays}
            setPhoneLineHolidays={setPhoneLineHolidays}
          />
        )
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleOnClose} maxWidth='md' fullWidth>
        <DialogTitle className='phone-line-dialog__header'>
          {isUpdate ? 'Edit AI Phone Line' : 'Add AI Phone Line'}
          <IconButton size='small' onClick={handleOnClose}>
            <Icon name='close' fontSize='small' />
          </IconButton>
        </DialogTitle>
        <DialogContent className='phone-line-dialog__content'>
          <WizardStepper
            steps={WIZARD_STEPS}
            currentStep={stepIndex}
            onStepClick={handleStepClick}
            allowStepSkipping
          />

          {error && (
            <Box
              className='phone-line-dialog__error'
              sx={{ backgroundColor: 'error.light', borderColor: 'error.main' }}
            >
              <Typography color='error.dark' variant='body2'>
                {error}
              </Typography>
            </Box>
          )}

          <Box className='phone-line-dialog__step'>{renderStep()}</Box>

          <Toast
            type={snackbarType}
            message={snackbarMessage}
            open={isSnackbarOpen}
            onClose={() => setIsSnackbarOpen(false)}
            autoHideDuration={3000}
          />
          {isSaving && (
            <Box className='phone-line-dialog__saving-overlay'>
              <CircularProgress size={48} />
              <Typography className='phone-line-dialog__saving-text'>
                {isUpdate ? 'Updating AI Phone Line...' : 'Adding AI Phone Line...'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions className='phone-line-dialog__actions'>
          <Button variant='outlined' onClick={handleOnClose}>
            Cancel
          </Button>
          <Box className='phone-line-dialog__actions-nav'>
            {stepIndex > 0 && (
              <Button variant='outlined' onClick={handleBack}>
                Back
              </Button>
            )}
            {!isLastStep && (
              <Button variant='outlined' onClick={handleNext}>
                Next
              </Button>
            )}
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={!isValidForm || (isUpdate && !hasFormChanged())}
            >
              {isUpdate ? 'Update Phone Line' : 'Save Phone Line'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(showNoTransferFallbackWarning)}
        onClose={() => setShowNoTransferFallbackWarning(false)}
      >
        <DialogTitle className='phone-line-dialog__warning-title'>No Transfer Fallback</DialogTitle>
        <DialogContent>
          <Typography>
            No default transfer line or voicemail intake is set up so transferred calls will not
            have a fallback. Do you want to save anyway?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant='text'
            onClick={() => {
              setShowNoTransferFallbackWarning(false)
              executeSave()
            }}
          >
            Save Anyway
          </Button>
          <Button variant='outlined' onClick={() => setShowNoTransferFallbackWarning(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AIPhoneLineDialog
